// @ts-nocheck - untyped
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import Notification from '../../Layout/Notification';
import UserManagementService from '../../../Services/UserManagementService';
import ConfirmationDialog from './ConfirmationDialog';
import { alpha, useTheme } from '@mui/material/styles';
import { validateTenantData } from '../../../Utils/formValidation';

const StatusPill = ({ active }: { active: boolean }) => {
  const theme = useTheme();
  const fg = active ? theme.tokens.successFg : theme.tokens.dangerFg;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: 6,
        color: fg,
        backgroundColor: active ? theme.tokens.successBg : theme.tokens.dangerBg,
        border: `1px solid ${alpha(fg, 0.2)}`,
      }}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

type UserRow = Record<string, unknown>;

const userColumns: MRT_ColumnDef<UserRow>[] = [
  { accessorKey: 'firstName', header: 'First Name', size: 150 },
  { accessorKey: 'lastName', header: 'Last Name', size: 150 },
  { accessorKey: 'email', header: 'Email Address', size: 250 },
  {
    accessorFn: (r: UserRow) => String(r.activated),
    id: 'activated',
    header: 'Active',
    size: 100,
    enableSorting: false,
    Cell: ({ row }) => <StatusPill active={Boolean(row.original.activated)} />,
  },
];

interface TenantDetailsProps {
  open: boolean;
  tenant: Record<string, unknown> | null;
  onClose: () => void;
  onUpdateData: () => void;
}

const TenantDetails = ({ open, tenant, onClose, onUpdateData }: TenantDetailsProps) => {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const { id, name, tenantKey } = tenant || {};
  const [formData, setFormData] = useState({
    id: id || '',
    name: name || '',
    tenantKey: tenantKey || '',
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [createUpdateConfirmationOpen, setCreateUpdateConfirmationOpen] = useState(false);

  const handleFormChange = ({ target }) =>
    setFormData(p => ({ ...p, [target.name]: target.value }));
  const handleCreateUpdateButtonClick = () => {
    if (validateTenantData(formData)) setCreateUpdateConfirmationOpen(true);
  };

  const handleCreateUpdateConfirmationConfirm = async () => {
    try {
      setLoading(true);
      setCreateUpdateConfirmationOpen(false);
      await (tenant ? handleTenantUpdate() : handleTenantCreation());
      onUpdateData();
      onClose();
    } catch (error) {
      Notification.showError(error.response?.data || 'An unexpected error occurred.');
      setLoading(false);
    }
  };

  const handleTenantCreation = async () => {
    const newTenant = await UserManagementService.createTenant({
      name: formData.name,
      tenantKey: formData.tenantKey,
    });
    Notification.showSuccess(`Tenant ${newTenant.name} created successfully.`);
  };

  const handleTenantUpdate = async () => {
    const updatedTenant = await UserManagementService.updateTenant({
      id,
      name: formData.name,
      tenantKey: formData.tenantKey,
    });
    Notification.showSuccess(`Tenant ${updatedTenant.name} updated successfully.`);
  };

  const fetchData = useCallback(async () => {
    if (!tenantKey) return;
    setLoading(true);
    try {
      const response = await UserManagementService.getUsersByTenantKey(tenantKey);
      setUsers(response.data);
    } catch (err) {
      Notification.showError(`Unexpected error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [tenantKey]);

  useEffect(() => {
    if (!open) return;
    if (tenant) {
      setFormData({
        id: tenant.id || '',
        name: tenant.name || '',
        tenantKey: tenant.tenantKey || '',
      });
      fetchData();
    } else {
      setFormData({ id: '', name: '', tenantKey: '' });
      setUsers([]);
    }
  }, [open, tenant, fetchData]);

  const handleDeleteConfirmationDelete = async () => {
    setDeleteConfirmationOpen(false);
    setLoading(true);
    try {
      if (tenant) {
        await UserManagementService.deleteTenant(id);
        Notification.showSuccess('Tenant deleted successfully.');
        onUpdateData();
        onClose();
      }
    } catch (error) {
      Notification.showError(`Error deleting tenant: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const table = useMaterialReactTable({
    data: users,
    columns: userColumns,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    initialState: { density: 'compact' },
    state: { isLoading: loading },
    paginationDisplayMode: 'pages',
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 2, border: '1px solid var(--t-border, rgba(0,0,0,0.12))' },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 700,
        fontSize: 13,
        backgroundColor: 'var(--t-surface-subtle)',
        borderBottom: `2px solid ${ACCENT}`,
      },
    },
    muiTableBodyCellProps: { sx: { fontSize: 13 } },
  });

  const labelSx = {
    textAlign: 'left',
    fontSize: '13px',
    color: 'var(--t-fg)',
    mb: 0.5,
    fontWeight: 500,
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={users.length > 0 ? 'md' : 'sm'}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'var(--t-surface)',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {tenant ? 'Edit Tenant' : 'Create Tenant'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tenant ? 'Update tenant information' : 'Create a new tenant'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'var(--t-fg-muted)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5, pb: 1, position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.6)',
                zIndex: 10,
                borderRadius: 3,
              }}
            >
              <div className="spinner" />
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <InputLabel htmlFor="tenant-name" sx={labelSx}>
              Tenant Name *
            </InputLabel>
            <TextField
              hiddenLabel
              id="tenant-name"
              name="name"
              size="small"
              value={formData.name || ''}
              onChange={handleFormChange}
              fullWidth
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <InputLabel htmlFor="tenant-key" sx={labelSx}>
              Tenant Key *
            </InputLabel>
            <TextField
              hiddenLabel
              id="tenant-key"
              name="tenantKey"
              size="small"
              value={formData.tenantKey || ''}
              onChange={handleFormChange}
              fullWidth
            />
          </Box>

          {users.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Users in this tenant ({users.length})
              </Typography>
              <MaterialReactTable table={table} />
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          {tenant && users.length === 0 ? (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setDeleteConfirmationOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Delete Tenant
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'var(--t-fg-muted)',
                borderColor: 'var(--t-border)',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateUpdateButtonClick}
              sx={{
                backgroundColor: ACCENT,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: theme.tokens.accentHover },
              }}
            >
              {tenant ? 'Save Changes' : 'Create Tenant'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={createUpdateConfirmationOpen}
        onClose={() => setCreateUpdateConfirmationOpen(false)}
        onConfirm={handleCreateUpdateConfirmationConfirm}
        title={`Confirm ${tenant ? 'Update' : 'Creation'}`}
        description={`Are you sure you want to ${tenant ? 'update' : 'create'} this tenant?`}
      />
      {tenant && users.length === 0 && (
        <ConfirmationDialog
          open={deleteConfirmationOpen}
          onClose={() => setDeleteConfirmationOpen(false)}
          onConfirm={handleDeleteConfirmationDelete}
          title="Confirm Deletion"
          description="Are you sure you want to delete this tenant?"
        />
      )}
    </>
  );
};

export default TenantDetails;
